import type { FetchResponse, ResponseType } from 'ofetch'
import { parseQuery, parseURL, stringifyParsedURL } from 'ufo'
import { omitBy } from 'lodash-es'
import { Message } from '@arco-design/web-vue'

export default defineNuxtPlugin(() => {
  const { apiV2, ENV } = useRuntimeConfig().public

  const request = $fetch.create({
    baseURL: apiV2,
    retry: false,

    onRequest: (ctx) => {
      if (import.meta.server) {
        // Forward cookies to the target
        ctx.options.headers = useRequestHeaders(['cookie'])

        if (!import.meta.prerender) {
          const { host } = useRequestHeaders(['host'])
          if (host) {
            const protocal = import.meta.dev || ENV === 'dev' ? 'http' : 'https'
            const baseURL = ctx.options?.baseURL || apiV2
            // 检查 baseURL 是否以 /api/admin/v1 结尾
            if (baseURL.endsWith('/api/admin/v1')) {
              ctx.options.baseURL = baseURL
            }
            else {
              ctx.options.baseURL = `${protocal}://${host}${apiV2}`
            }
          }
        }
      }

      if (ctx.options.query) {
        // 👇过滤掉值为 undefined || null || '' || -1 的参数
        ctx.options.query = omitBy(
          ctx.options.query,
          val => typeof val === 'undefined' || val === null || val === '' || val === -1,
        )

        // 👇自己处理query的原因
        // https://github.com/unjs/ufo/issues/154
        const url = parseURL(ctx.request.toString())
        const query = new URLSearchParams()
        Object
          .entries({ ...parseQuery(url.search), ...ctx.options.query })
          .forEach(([key, value]) => {
            query.append(key, value)
          })
        url.search = query.toString()
        ctx.request = stringifyParsedURL(url)
        ctx.options.query = undefined // cleaning up the query so that ofetch doesn't process it again
      }
    },

    onResponse: ({ request, response, options }) => {
      if (options.responseType === 'blob') {
        response._data = {
          data: response._data,
          headers: response.headers,
        }
      }
      else if (response._data.success) {
        const success = response._data.success
        const result = success.isCrypto ? JSON.parse(decryptData(success.result)) : success.result
        response._data = result
      }
      else {
        handleError(request.toString(), response, options)
      }
    },

    onRequestError: ({ request, error }) => {
      if (error.name !== 'AbortError') {
        console.error('[RequestError]', request, error?.message)
      }
    },

    onResponseError: ({ response, options }) => {
      const { _data } = response
      if (_data && _data.data instanceof Blob) {
        // 处理文件流错误
        const reader = new FileReader()
        reader.onloadend = () => {
          const result = reader.result
          try {
            const errorData = JSON.parse(result as string)
            response._data.fault = errorData?.fault
            handleError(request.toString(), response, options)
          }
          catch {
            response._data.fault = { message: 'Failed to parse error data' }
            handleError(request.toString(), response, options)
          }
        }
        reader.readAsText(_data.data)
      }
      else {
        throw response?._data?.fault
      }
    },
  })

  function handleError(request: string, response: FetchResponse<any> & FetchResponse<ResponseType>, options?: { isCustomError?: boolean }) {
    const err = (text?: string) => {
      if (import.meta.client) {
        Message.error(response?._data?.fault?.message ?? text)
      }
      else {
        console.error(`[code: ${response?._data?.fault?.code}]`, request, response?._data?.fault?.message ?? text)
      }
    }
    if (!response._data) {
      err('Request Timeout! The Server did not respond.')
      return
    }
    const { toggleDialog, setUserInfo, userRole } = useLogin()
    const { toggleExpried } = useExpired()
    const { open } = useOffLineModal()
    const route = useRoute()
    const noModals = route.meta.noModals
    const handleStatusMap: { [key: number]: () => void } = {
      401: () => {
        const { code, message } = response?._data?.fault
        if (code >= 40100 && code <= 40109) { // 401代表未登录，其他状态暂不处理
          setUserInfo()
          if ([40101, 40102, 40103].includes(code)) {
            open(code, message)
            return
          }
          // eslint-disable-next-line ts/no-unused-expressions
          !noModals && toggleDialog(true)
        }
        else if (code >= 40110 && code <= 40119 && userRole.value?.resource !== 1) {
          toggleExpried(true, options?.expiredType || code)
        }
        else {
          err()
        }
      },
    }

    const handler = handleStatusMap[response.status]
    if (handler) {
      handler()
    }
    else {
      // eslint-disable-next-line ts/no-unused-expressions
      !options?.isCustomError && err('Unknown Error!')
    }
  }

  // Expose to useNuxtApp().$request
  return {
    provide: {
      request,
    },
  }
})
